import {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Fragment, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { GoPlus } from "react-icons/go";

import FormHeader from "@/components/FormHeader";
import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import Combobox from "@/components/Combobox";
import useApiBaseUrl from "@/hooks/useApiBaseUrl";
import { useDebounce } from "@/hooks/useDebounce";
import { useTahun } from "@/hooks/useTahun";
import api from "@/lib/axios";
import { formatRupiah } from "@/lib/utils";
import {
  getListJalurJenis,
  getListPeriode,
  getListUnitkerja,
} from "@/services/getData";
import {
  DataPendaftaran,
  JalurJenis,
  PaginationData,
  Periode,
  Unitkerja,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  Banknote,
  BoxSelect,
  Briefcase,
  CheckCircle2,
  Clock,
  Droplets,
  ExternalLink,
  FileCheck,
  FileText,
  HeartPulse,
  Home,
  IdCard,
  ListChecks,
  Loader2,
  Map,
  MapPin,
  Phone,
  Receipt,
  Settings2,
  User,
  Users,
  XCircle,
  Zap,
} from "lucide-react";
import toast from "react-hot-toast";
import { FaUserGraduate } from "react-icons/fa6";
import { LuListFilter } from "react-icons/lu";
import { MdSync } from "react-icons/md";
import { RiFileExcel2Fill } from "react-icons/ri";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import FormFilter from "./FormFilter";
import FormImport from "./FormImport";
import FormImportDataSiswaLulus from "./FormImportDataSiswaLulus";
import FormImportDataSiswaLulusAllPtn from "./FormImportDataSiswaLulusAllPtn";
import FormUpdate from "./FormUpdate";
import FormUpdateRpl from "./FormUpdateRpl";
import { Pagination } from "./Pagination";

interface DataTableProps<TData extends DataPendaftaran, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  paginationData: PaginationData<DataPendaftaran[]>;
  paginationDataPendaftaranProfesi: PaginationData<DataPendaftaran[]>;
  paginationDataPendaftaranRpl: PaginationData<DataPendaftaran[]>;
  isPending: boolean;
}

const postDataSyncSiswaLulus = async (id_sys_periode: string) => {
  const response = await api.post("/penerimaan/sync/data-siswa-lulus", {
    id_sys_periode,
  });
  return response.data;
};

const postDataPenetapanUkt = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post("/penerimaan/sync/data-ukt-lulus", {
    id_sys_periode,
  });
  return response.data;
};

const postDataSyncPembayaranPendaftaran = async ({
  id_sys_periode,
}: {
  id_sys_periode: string;
}) => {
  const response = await api.post("/sinkron/pembayaran-pendaftaran/sync", {
    id_sys_periode,
  });
  return response.data;
};

export function DataTable<TData extends DataPendaftaran, TValue>({
  columns,
  data,
  paginationData,
  paginationDataPendaftaranProfesi,
  paginationDataPendaftaranRpl,
  isPending: isPendingDataPendaftaran,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { apiBaseUrl } = useApiBaseUrl();
  const [searchParams] = useSearchParams();
  const ids_ref_jalur_jenis = searchParams.get("ids_ref_jalur_jenis");
  const ids_uk = searchParams.get("ids_uk");

  const { selectedTahun } = useTahun();

  const [form, setForm] = useState({
    id_sys_periode: "",
    tahun: `${new Date().getFullYear()}` || "",
  });

  // const [selected, setSelected] = useState<DataPendaftaran | null>(null);
  const [showMenuSync, setShowMenuSync] = useState(false);
  const [showMenuImport, setShowMenuImport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showImportDataSiswaLulus, setShowImportDataSiswaLulus] =
    useState(false);
  const [showImportDataSiswaLulusAllPtn, setShowDataSiswaLulusAllPtn] =
    useState(false);
  const [showSync, setShowSync] = useState(false);
  const [showSyncPembayaranPendaftaran, setShowSyncPembayaranPendaftaran] =
    useState(false);
  const [showSyncUkt, setShowSyncUkt] = useState(false);
  const [openPeriodeSync, setOpenPeriodeSync] = useState(false);
  const [
    openPeriodeSyncPembayaranPendaftaran,
    setOpenPeriodeSyncPembayaranPendaftaran,
  ] = useState(false);
  const [openPeriodeSyncUkt, setOpenPeriodeSyncUkt] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showUpdateSiswaLulusProfesi, setShowUpdateSiswaLulusProfesi] =
    useState(false);
  const [showUpdateSiswaLulusRpl, setShowUpdateSiswaLulusRpl] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );
  const debouncedQuery = useDebounce(searchQuery, 1000);
  const navigate = useNavigate();
  const location = useLocation();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      expanded,
    },
  });

  const { data: listJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  });
  const ids_ref_jalur_jenis_array = ids_ref_jalur_jenis?.split("-");
  const listJalurJenisAktifFilter =
    listJalurJenis?.filter((item: JalurJenis) => {
      return ids_ref_jalur_jenis_array?.includes(
        String(item.id_ref_jalur_jenis),
      );
    }) || [];

  const { data: listUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  });
  const listProdi = listUnitkerja?.filter((unitkerja: Unitkerja) => {
    return unitkerja.unitkerja_group_key == "5" && unitkerja.is_active;
  });
  const ids_uk_array = ids_uk?.split("-");
  const listProdiAktifFilter =
    listProdi?.filter((item: Unitkerja) => {
      return ids_uk_array?.includes(String(item.uk_id));
    }) || [];

  const { data: dataPeriode, isPending: isPendingDataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  });

  const optionsPeriode =
    dataPeriode?.map((periode: Periode) => ({
      label: `${periode.tahun} - ${periode.jalur_jenis?.nama_jalur_jenis} - Gelombang ${periode.gelombang}`,
      value: `${periode.id_sys_periode}`,
    })) || [];
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: postDataSyncSiswaLulus,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] });
      setShowSync(false);
      setForm((prev) => ({ ...prev, id_ref_jalur_jenis: "" }));
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const { mutate: mutateSyncUkt, isPending: isPendingSyncUkt } = useMutation({
    mutationFn: postDataPenetapanUkt,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] });
      setShowSyncUkt(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const {
    mutate: mutateSyncPembayaranPendaftaran,
    isPending: isPendingSyncPembayaranPendaftaran,
  } = useMutation({
    mutationFn: postDataSyncPembayaranPendaftaran,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] });
      setShowSyncPembayaranPendaftaran(false);
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSyncDataSiswaLulus = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutate(form.id_sys_periode);
  };

  const handleSyncDataPembayaranPendaftaran = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutateSyncPembayaranPendaftaran({
      id_sys_periode: form.id_sys_periode,
    });
  };

  const handleSyncDataPenetapanUkt = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutateSyncUkt({
      id_sys_periode: form.id_sys_periode,
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (debouncedQuery) {
      // if (params.get("search") !== debouncedQuery) {
      //   params.delete("page");
      // }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search]);

  const renderExpandedContent = (dataPendaftaran: DataPendaftaran) => {
    // Helper variables
    const dataSiswa = dataPendaftaran?.data_siswa;
    const pathFoto = dataSiswa?.path_foto;
    const dataBerkas = dataPendaftaran?.data_berkas || [];

    return (
      <div className="p-4 bg-muted/50 space-y-4">
        {/* === BAGIAN ATAS: INFORMASI UTAMA (GRID 5 KOLOM) === */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* --- KOLOM 1: KARTU FOTO --- */}
          <Card className="lg:col-span-1 border-primary/20 shadow-sm h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-center">
                Pas Foto
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center pt-2 pb-6">
              <div className="relative w-full max-w-[180px] aspect-[3/4] rounded-lg overflow-hidden border-2 border-muted bg-slate-100 shadow-sm group">
                {pathFoto ? (
                  <a href={pathFoto} target="_blank" rel="noreferrer">
                    <img
                      src={pathFoto}
                      alt="Foto Siswa"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                    />
                  </a>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center text-muted-foreground">
                    <span className="text-xs">Tidak ada foto</span>
                  </div>
                )}
              </div>
              <div className="mt-4 text-center">
                <Badge variant="outline" className="text-[10px]">
                  ID: {dataSiswa?.id_data_siswa ?? "-"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* --- KOLOM 2: BIODATA --- */}
          <Card className="lg:col-span-1 h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Biodata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  Nama Lengkap
                </span>
                <p className="text-sm font-semibold text-foreground leading-tight">
                  {dataPendaftaran?.data_siswa?.nama_lengkap}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  NISN
                </span>
                <p className="text-sm font-semibold text-foreground leading-tight">
                  {dataPendaftaran?.data_siswa?.nisn}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  NIK
                </span>
                <p className="text-sm font-semibold text-foreground leading-tight">
                  {dataPendaftaran?.data_siswa?.nik}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  Email
                </span>
                <p className="text-sm text-foreground break-all">
                  {dataPendaftaran?.data_siswa?.email}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  Alamat
                </span>
                <p className="text-sm text-foreground break-all">
                  {[
                    dataPendaftaran?.data_siswa?.jalan,
                    dataPendaftaran?.data_siswa?.ref_wilayah?.nama_kecamatan,
                    dataPendaftaran?.data_siswa?.ref_wilayah?.nama_kabupaten,
                    dataPendaftaran?.data_siswa?.ref_wilayah?.nama_provinsi,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  No. HP
                </span>
                <p className="text-sm text-foreground">
                  {dataPendaftaran?.data_siswa?.handphone}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  Ukuran Jas/Baju
                </span>
                <p className="text-sm text-foreground">
                  {dataPendaftaran?.data_siswa?.ukuran_baju}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* --- KOLOM 3: DATA PENDAFTARAN --- */}
          <Card className="lg:col-span-1 h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Data Pendaftaran
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <span className="text-xs font-medium text-muted-foreground">
                    Tahun
                  </span>
                  <p className="text-sm">{dataPendaftaran?.tahun}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-muted-foreground">
                    KIPK
                  </span>
                  <p className="text-sm">{dataPendaftaran?.no_kipk ?? "-"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-1">
                <div>
                  <span className="text-xs font-medium text-muted-foreground">
                    Tahun Ajaran
                  </span>
                  <p className="text-sm">{dataPendaftaran?.tahun_ajaran}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-muted-foreground">
                    Semester
                  </span>
                  <p className="text-sm">{dataPendaftaran?.semester ?? "-"}</p>
                </div>
              </div>

              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  Jalur
                </span>
                <p
                  className="text-sm truncate"
                  title={dataPendaftaran?.periode?.nama_jalur_jenis}
                >
                  {dataPendaftaran?.periode?.nama_jalur_jenis}
                </p>
              </div>

              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  No. Pendaftaran
                </span>
                <p className="text-sm font-mono bg-slate-100 px-1 py-0.5 rounded w-fit">
                  {dataPendaftaran?.nomor_pendaftaran}
                </p>
              </div>

              <div className="pt-1">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-muted-foreground">
                    Validasi:
                  </span>
                  {!dataPendaftaran.is_valid_pendaftaran ? (
                    <Badge variant="secondary" className="text-[10px] px-1 h-5">
                      Belum
                    </Badge>
                  ) : (
                    <Badge className="text-[10px] px-1 h-5">Valid</Badge>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    Kelulusan:
                  </span>
                  {!dataPendaftaran.is_lulus ? (
                    <Badge variant="secondary" className="text-[10px] px-1 h-5">
                      Belum
                    </Badge>
                  ) : (
                    <Badge variant="success" className="text-[10px] px-1 h-5">
                      Lulus
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* --- KOLOM 4: PILIHAN PRODI --- */}
          <Card className="lg:col-span-1 h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Pilihan Prodi
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[...Array(4)].map((_, index) => {
                const pilihanProdi = dataPendaftaran.data_pilihan_prodi?.find(
                  (prodi) => prodi.urutan_program_studi === index + 1,
                );

                return (
                  <div
                    key={index}
                    className="border-b border-border/50 pb-1 last:border-0 last:pb-0"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-[10px] font-bold text-muted-foreground min-w-[15px] mt-0.5">
                        {index + 1}.
                      </span>
                      {pilihanProdi ? (
                        <div className="flex flex-col">
                          <span
                            className={`text-xs font-medium ${pilihanProdi.is_lulus ? "text-primary font-bold" : ""}`}
                          >
                            {pilihanProdi?.unitkerja?.nama} ({pilihanProdi?.pilihan_lokasi_kuliah})
                          </span>
                          {pilihanProdi?.is_lulus ? (
                            <Badge
                              variant="success"
                              className="w-fit text-[9px] h-4 px-1 mt-0.5"
                            >
                              Diterima Disini
                            </Badge>
                          ) : (
                            ""
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">
                          -
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* --- KOLOM 5: UKT & TAGIHAN --- */}
          <Card className="lg:col-span-1 h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                Keuangan & Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Tagihan Box */}
              <div className="bg-slate-50 border rounded-md p-2">
                <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                  Tagihan Pendaftaran
                </span>
                {dataPendaftaran?.data_tagihan_pendaftaran?.length ? (
                  dataPendaftaran.data_tagihan_pendaftaran.map((tagihan) => (
                    <div
                      key={tagihan.id_data_tagihan_daftar_plaza}
                      className="flex justify-between items-center text-xs mb-1 last:mb-0"
                    >
                      <span>
                        {tagihan.amount
                          ? formatRupiah(Number(tagihan.amount))
                          : "Rp 0"}
                      </span>
                      {tagihan.status_bayar == 2 ? (
                        <Badge
                          variant="success"
                          className="text-[9px] h-4 px-1"
                        >
                          Lunas
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="text-[9px] h-4 px-1"
                        >
                          Belum
                        </Badge>
                      )}
                    </div>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground italic">
                    Tidak ada tagihan
                  </span>
                )}
              </div>

              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    Validasi UKT
                  </span>
                  {!dataPendaftaran.is_valid_isi_ukt ? (
                    <Badge variant="secondary" className="text-[9px] h-4">
                      Belum
                    </Badge>
                  ) : (
                    <Badge className="text-[9px] h-4">Valid</Badge>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    Daftar Kembali
                  </span>
                  {!dataPendaftaran.is_daftar_kembali ? (
                    <Badge variant="secondary" className="text-[9px] h-4">
                      Belum
                    </Badge>
                  ) : (
                    <Badge className="text-[9px] h-4">Sudah</Badge>
                  )}
                </div>
              </div>

              <div className="pt-2 border-t">
                <span className="text-xs font-medium text-muted-foreground">
                  NIM Mahasiswa
                </span>
                <p className="text-lg font-bold text-primary tracking-tight">
                  {dataPendaftaran?.nim || "-"}
                </p>
              </div>
              <div className="pt-2 border-t">
                <span className="text-xs font-medium text-muted-foreground">
                  Email Undiksha
                </span>
                <p className="text-lg font-bold text-primary tracking-tight">
                  {dataPendaftaran?.email_sso || "-"}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* === BAGIAN: DATA KELUARGA === */}
        <Card>
          <CardHeader className="pb-2 border-b border-slate-100 dark:border-slate-800">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                Data Keluarga
                {dataPendaftaran?.data_keluarga && (
                  <Badge variant="secondary" className="ml-1 text-[10px]">
                    {dataPendaftaran.data_keluarga.length} Orang
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {dataPendaftaran?.data_keluarga &&
              dataPendaftaran.data_keluarga.length > 0 ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {dataPendaftaran.data_keluarga.map((anggota, idx) => (
                  <div
                    key={anggota.id_data_keluarga || idx}
                    className="flex flex-col border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm"
                  >
                    {/* Header Anggota Keluarga */}
                    <div className="bg-slate-50 dark:bg-slate-900/80 p-3 sm:px-4 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-full shrink-0">
                          <User className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-bold text-slate-900 dark:text-slate-100 text-sm truncate">
                            {anggota.nama || "Nama Tidak Diketahui"}
                          </h4>
                          <div className="flex items-center gap-2 mt-0.5">
                            {/* Catatan: Anda bisa memetakan id_ref_hubungan_keluarga ke String (Ayah, Ibu, dll) */}
                            <Badge
                              variant="outline"
                              className="text-[9px] uppercase tracking-wider bg-white dark:bg-slate-800"
                            >
                              Hubungan:{" "}
                              {anggota.hubungan_keluarga?.hubungan_keluarga ||
                                "-"}
                            </Badge>
                            <span className="text-[10px] text-slate-500 font-medium">
                              {anggota.jk === "L"
                                ? "Laki-laki"
                                : anggota.jk === "P"
                                  ? "Perempuan"
                                  : "-"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Status Verifikasi Badge */}
                      <Badge
                        variant={anggota.is_valid ? "success" : "secondary"}
                        className="text-[9px] shrink-0"
                      >
                        {anggota.is_valid ? "Valid" : "Belum Diverifikasi"}
                      </Badge>
                    </div>

                    {/* Konten Detail */}
                    <div className="p-3 sm:p-4 space-y-4 text-sm flex-1">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4">
                        {/* Kolom Kiri: Info Personal & Kontak */}
                        <div className="space-y-3">
                          <div className="flex items-start gap-2.5">
                            <IdCard className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">
                                NIK / Tempat, Tgl Lahir
                              </p>
                              <p className="font-medium text-slate-800 dark:text-slate-200">
                                {anggota.nik || "-"}
                                <br />
                                <span className="text-xs font-normal text-slate-600 dark:text-slate-400">
                                  {anggota.tempat_lahir || "-"},{" "}
                                  {anggota.tanggal_lahir || "-"}
                                </span>
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-2.5">
                            <HeartPulse className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">
                                Status Kehidupan
                              </p>
                              <p className="font-medium text-slate-800 dark:text-slate-200 text-xs">
                                {anggota.status === "M"
                                  ? "Meninggal"
                                  : anggota.status === "H"
                                    ? "Hidup"
                                    : anggota.status || "-"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-2.5">
                            <Phone className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">
                                No. Handphone
                              </p>
                              <p className="font-medium text-slate-800 dark:text-slate-200 text-xs">
                                {anggota.handphone || "-"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-2.5">
                            <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">
                                Alamat (Jalan)
                              </p>
                              <p className="font-medium text-slate-800 dark:text-slate-200 text-xs line-clamp-2">
                                {anggota.jalan || "-"}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Kolom Kanan: Ekonomi & Berkas */}
                        <div className="space-y-3">
                          <div className="flex items-start gap-2.5">
                            <Briefcase className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">
                                Pekerjaan
                              </p>
                              <p className="font-medium text-slate-800 dark:text-slate-200 text-xs">
                                Pekerjaan: {anggota.pekerjaan?.pekerjaan || "-"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-2.5">
                            <Banknote className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">
                                Penghasilan
                              </p>
                              <p className="font-medium text-emerald-600 dark:text-emerald-400 text-sm">
                                {anggota.penghasilan_old !== null &&
                                  anggota.penghasilan_old !== undefined
                                  ? formatRupiah(
                                    Number(anggota.penghasilan_old),
                                  )
                                  : "-"}
                              </p>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                            <p className="text-[10px] uppercase font-bold text-slate-500 mb-2">
                              Dokumen Pendukung
                            </p>
                            <a
                              href={anggota.path_slip_gaji || "#"}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`group flex items-center gap-3 p-2.5 rounded-lg border transition-all duration-200 ${anggota.path_slip_gaji
                                ? "bg-slate-50 hover:bg-slate-100 hover:border-blue-300 dark:bg-slate-950 dark:hover:bg-slate-900/80 dark:border-slate-800"
                                : "bg-slate-50/50 opacity-60 cursor-not-allowed dark:bg-slate-900/40 border-dashed"
                                }`}
                              onClick={(e) =>
                                !anggota.path_slip_gaji && e.preventDefault()
                              }
                            >
                              <div
                                className={`p-1.5 rounded-md shrink-0 ${anggota.path_slip_gaji ? "bg-blue-100/50 text-blue-600 group-hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400" : "bg-slate-200 text-slate-400"}`}
                              >
                                {anggota.path_slip_gaji ? (
                                  <FileCheck className="w-3.5 h-3.5" />
                                ) : (
                                  <XCircle className="w-3.5 h-3.5" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 line-clamp-1">
                                  Slip Gaji / Surat Penghasilan
                                </p>
                                <p className="text-[9px] text-muted-foreground">
                                  {anggota.path_slip_gaji
                                    ? "Unduh Dokumen"
                                    : "Tidak Dilampirkan"}
                                </p>
                              </div>
                              {anggota.path_slip_gaji && (
                                <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Catatan Verifikator */}
                      {!anggota.is_valid && anggota.catatan_verifikasi && (
                        <div className="mt-3 p-2.5 bg-red-50 border border-red-100 rounded-lg dark:bg-red-900/10 dark:border-red-900/30">
                          <p className="text-[10px] font-bold text-red-700 dark:text-red-400 mb-0.5">
                            Catatan Verifikasi:
                          </p>
                          <p className="text-xs text-red-600 dark:text-red-300">
                            {anggota.catatan_verifikasi}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                <Users className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm">
                  Tidak ada data keluarga yang dilaporkan
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* === BAGIAN BAWAH: DATA ASET (TANAH & BANGUNAN) === */}
        <Card>
          <CardHeader className="pb-2 border-b border-slate-100 dark:border-slate-800">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-emerald-600 dark:text-emerald-500" />
                Data Aset (Tanah & Bangunan)
              </div>
              {dataPendaftaran?.data_aset &&
                dataPendaftaran.data_aset.length > 0 && (
                  <Badge
                    // Cukup evaluasi nilai is_valid secara langsung (Truthy/Falsy)
                    variant={
                      dataPendaftaran.data_aset[0].is_valid
                        ? "success"
                        : "secondary"
                    }
                    className="text-[10px]"
                  >
                    {dataPendaftaran.data_aset[0].is_valid
                      ? "Valid"
                      : "Belum Diverifikasi"}
                  </Badge>
                )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {dataPendaftaran?.data_aset &&
              dataPendaftaran.data_aset.length > 0 ? (
              dataPendaftaran.data_aset.map((aset, idx) => (
                <div key={aset.id_data_aset || idx} className="space-y-6">
                  {/* INFO UTAMA ASET */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Luas Tanah */}
                    <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800 flex items-start gap-3">
                      <div className="p-2 bg-amber-100/50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-500 rounded-md shrink-0">
                        <Map className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider mb-0.5">
                          Luas Tanah
                        </p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {aset.aset_luas_tanah
                            ? `${aset.aset_luas_tanah} m²`
                            : "-"}
                        </p>
                      </div>
                    </div>

                    {/* Luas Bangunan */}
                    <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800 flex items-start gap-3">
                      <div className="p-2 bg-blue-100/50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-500 rounded-md shrink-0">
                        <BoxSelect className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider mb-0.5">
                          Luas Bangunan
                        </p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {aset.aset_luas_bangunan
                            ? `${aset.aset_luas_bangunan} m²`
                            : "-"}
                        </p>
                      </div>
                    </div>

                    {/* Listrik */}
                    <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800 flex items-start gap-3">
                      <div className="p-2 bg-yellow-100/50 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-500 rounded-md shrink-0">
                        <Zap className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider mb-0.5">
                          Daya Listrik
                        </p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {/* TODO: Map id_ref_aset_tegangan_listrik ke Label aslinya. 
                              Contoh: ID 3 mungkin = 900 VA */}
                          ID Ref: {aset.id_ref_aset_tegangan_listrik || "-"}
                        </p>
                      </div>
                    </div>

                    {/* Air */}
                    <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800 flex items-start gap-3">
                      <div className="p-2 bg-cyan-100/50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-500 rounded-md shrink-0">
                        <Droplets className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider mb-0.5">
                          Biaya Air /Bulan
                        </p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {aset.pembayaran_air !== null &&
                            aset.pembayaran_air !== undefined
                            ? formatRupiah(Number(aset.pembayaran_air))
                            : "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* BERKAS PENDUKUNG ASET */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-800 pb-2">
                      Dokumen Pendukung Aset
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Berkas Listrik */}
                      <a
                        href={aset.path_berkas_listrik || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`group flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 ${aset.path_berkas_listrik
                          ? "bg-white hover:bg-slate-50 hover:border-yellow-300 dark:bg-slate-950 dark:hover:bg-slate-900 dark:border-slate-800"
                          : "bg-slate-50 opacity-60 cursor-not-allowed dark:bg-slate-900/40"
                          }`}
                        onClick={(e) =>
                          !aset.path_berkas_listrik && e.preventDefault()
                        }
                      >
                        <div
                          className={`p-2 rounded-md ${aset.path_berkas_listrik ? "bg-yellow-100/50 text-yellow-600 group-hover:bg-yellow-100 dark:bg-yellow-900/30" : "bg-slate-200 text-slate-400"}`}
                        >
                          {aset.path_berkas_listrik ? (
                            <FileCheck className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 line-clamp-1">
                            Bukti Rekening Listrik
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {aset.path_berkas_listrik
                              ? "Lihat Dokumen"
                              : "Tidak Dilampirkan"}
                          </p>
                        </div>
                        {aset.path_berkas_listrik && (
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </a>

                      {/* Berkas Air */}
                      <a
                        href={aset.path_berkas_air || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`group flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 ${aset.path_berkas_air
                          ? "bg-white hover:bg-slate-50 hover:border-cyan-300 dark:bg-slate-950 dark:hover:bg-slate-900 dark:border-slate-800"
                          : "bg-slate-50 opacity-60 cursor-not-allowed dark:bg-slate-900/40"
                          }`}
                        onClick={(e) =>
                          !aset.path_berkas_air && e.preventDefault()
                        }
                      >
                        <div
                          className={`p-2 rounded-md ${aset.path_berkas_air ? "bg-cyan-100/50 text-cyan-700 group-hover:bg-cyan-100 dark:bg-cyan-900/30" : "bg-slate-200 text-slate-400"}`}
                        >
                          {aset.path_berkas_air ? (
                            <FileCheck className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 line-clamp-1">
                            Bukti Rekening Air
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {aset.path_berkas_air
                              ? "Lihat Dokumen"
                              : "Tidak Dilampirkan"}
                          </p>
                        </div>
                        {aset.path_berkas_air && (
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </a>

                      {/* Berkas PBB */}
                      <a
                        href={aset.path_berkas_pbb || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`group flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 ${aset.path_berkas_pbb
                          ? "bg-white hover:bg-slate-50 hover:border-emerald-300 dark:bg-slate-950 dark:hover:bg-slate-900 dark:border-slate-800"
                          : "bg-slate-50 opacity-60 cursor-not-allowed dark:bg-slate-900/40 dark:border-slate-800"
                          }`}
                        onClick={(e) =>
                          !aset.path_berkas_pbb && e.preventDefault()
                        }
                      >
                        <div
                          className={`p-2 rounded-md ${aset.path_berkas_pbb ? "bg-emerald-100/50 text-emerald-600 group-hover:bg-emerald-100 dark:bg-emerald-900/30" : "bg-slate-200 text-slate-400 dark:bg-slate-800"}`}
                        >
                          {aset.path_berkas_pbb ? (
                            <Receipt className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 line-clamp-1">
                            Bukti PBB / Pajak
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {aset.path_berkas_pbb
                              ? "Lihat Dokumen"
                              : "Tidak Dilampirkan"}
                          </p>
                        </div>
                        {aset.path_berkas_pbb && (
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </a>
                    </div>
                  </div>

                  {/* Catatan Verifikator (Jika ada & Jika tidak valid) */}
                  {!aset.is_valid && aset.catatan_verifikasi && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/10 dark:border-red-900/50">
                      <p className="text-xs font-bold text-red-700 dark:text-red-400 mb-1">
                        Catatan Verifikasi (Aset):
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-300">
                        {aset.catatan_verifikasi}
                      </p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground border-2 border-dashed rounded-lg bg-slate-50 dark:bg-slate-900/50 dark:border-slate-800">
                <Home className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm">Tidak ada data aset yang dilaporkan</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* === BAGIAN BAWAH: DATA BERKAS (FULL WIDTH) === */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Dokumen Berkas Mahasiswa
              <Badge variant="secondary" className="ml-2">
                {dataBerkas.length} File
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dataBerkas.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {dataBerkas.map((berkas, idx) => (
                  <a
                    key={idx}
                    href={berkas.path_berkas}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-slate-50 hover:border-primary/50 transition-all duration-200"
                  >
                    <div className="mt-1 bg-primary/10 p-2 rounded-md group-hover:bg-primary/20 transition-colors">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground line-clamp-2 leading-snug group-hover:text-primary transition-colors">
                        {berkas.sys_berkas?.jenis_berkas?.nama_jenis_berkas ||
                          "Dokumen Tanpa Nama"}
                      </p>
                      <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
                        <span>Lihat Dokumen</span>
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground dark:text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                <FileText className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm">Tidak ada berkas yang diunggah</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* === BAGIAN BAWAH: DATA STEP (PROGRESS PENDAFTARAN) === */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-slate-500" />
              Kelengkapan Data Pendaftaran (Step)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dataPendaftaran?.data_step ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 pt-2">
                {(() => {
                  // Destructure data_step untuk mempermudah akses
                  const steps = dataPendaftaran.data_step;

                  // Buat array konfigurasi untuk setiap step yang ingin ditampilkan
                  const stepItems = [
                    {
                      key: "data_diri",
                      label: "Biodata Diri",
                      isComplete: steps.data_diri,
                    },
                    {
                      key: "is_confirm_jurusan_sekolah",
                      label: "Konfirmasi Sekolah",
                      isComplete: steps.is_confirm_jurusan_sekolah,
                    },
                    {
                      key: "data_keluarga",
                      label: "Data Keluarga",
                      isComplete: steps.data_keluarga,
                    },
                    {
                      key: "data_tanah_bangunan",
                      label: "Tanah & Bangunan",
                      isComplete: steps.data_tanah_bangunan,
                    },
                    {
                      key: "data_kendaraan",
                      label: "Data Kendaraan",
                      isComplete: steps.data_kendaraan,
                    },
                    {
                      key: "data_rapor",
                      label: "Data Rapor",
                      isComplete: steps.data_rapor,
                    },
                    {
                      key: "data_prestasi",
                      label: "Data Prestasi",
                      isComplete: steps.data_prestasi,
                    },
                    {
                      key: "data_portofolio",
                      label: "Data Portofolio",
                      isComplete: steps.data_portofolio,
                    },
                    {
                      key: "data_kepemimpinan",
                      label: "Kepemimpinan",
                      isComplete: steps.data_kepemimpinan,
                    },
                    {
                      key: "data_pilihan_prodi",
                      label: "Pilihan Prodi",
                      isComplete: steps.data_pilihan_prodi,
                    },
                    {
                      key: "valid_is_utbk_pusat",
                      label: "Nilai UTBK Pusat",
                      isComplete: steps.valid_is_utbk_pusat,
                    },
                    {
                      key: "data_berkas",
                      label: "Upload Berkas",
                      isComplete: steps.data_berkas,
                    },
                    {
                      key: "valid_is_ukt",
                      label: "Validasi UKT",
                      isComplete: steps.valid_is_ukt,
                    },
                    {
                      key: "is_resume_pendaftaran",
                      label: "Resume Pendaftaran",
                      isComplete: steps.is_resume_pendaftaran,
                    },
                    {
                      key: "valid_is_pendaftaran",
                      label: "Finalisasi (Permanen)",
                      isComplete: steps.valid_is_pendaftaran,
                    },
                  ];

                  return stepItems.map((item, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${item.isComplete
                        ? "bg-green-50/30 border-green-100 dark:bg-green-950/10 dark:border-green-900/30"
                        : "bg-slate-50/50 border-slate-100 dark:bg-slate-900/50 dark:border-slate-800"
                        }`}
                    >
                      <div
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${item.isComplete
                          ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                          }`}
                      >
                        {item.isComplete ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <Clock className="w-4 h-4" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate">
                          {item.label}
                        </p>
                        <p
                          className={`text-[10px] ${item.isComplete ? "text-green-600 dark:text-green-400 font-medium" : "text-muted-foreground"}`}
                        >
                          {item.isComplete ? "Lengkap" : "Belum Lengkap"}
                        </p>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-muted-foreground border border-dashed rounded-lg bg-slate-50 dark:bg-slate-900/50 dark:border-slate-800">
                <p className="text-sm">Informasi data step tidak tersedia.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col gap-4 py-4">
        {/* --- BARIS UTAMA: SEARCH, FILTER, & ACTIONS --- */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          {/* Kiri: Search & Filter */}
          <div className="flex flex-1 items-center gap-2 w-full md:max-w-md">
            {/* Input Search dengan Ikon (Opsional tapi disarankan) */}
            <div className="relative w-full">
              {/* <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /> */}
              <Input
                autoFocus
                placeholder="Cari tahun, no. pendaftaran, nama, status..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full" // Tambahkan pl-9 jika memakai ikon Search di atas
              />
            </div>

            <Button
              variant={ids_ref_jalur_jenis ? "default" : "outline"}
              onClick={() => setShowFilter(true)}
              className="shrink-0"
            >
              <LuListFilter className="mr-2 w-4 h-4" />
              Filter
            </Button>
          </div>

          {/* Kanan: Aksi Data & View */}
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <Button
              variant="outline"
              className="bg-white dark:bg-slate-950"
              onClick={() => setShowMenuImport(true)}
            >
              <GoPlus className="mr-2 w-4 h-4" />
              Import
            </Button>

            <Button
              variant="outline"
              className="bg-white dark:bg-slate-950"
              onClick={() => {
                const url = new URL(`${apiBaseUrl}/data-pendaftaran/export`);
                const params = new URLSearchParams(window.location.search);
                if (selectedTahun) params.set("tahun", selectedTahun?.label);
                url.search = params.toString();
                window.open(url.toString(), "_blank");
              }}
            >
              <RiFileExcel2Fill className="mr-2 w-4 h-4 text-emerald-600 dark:text-emerald-500" />
              Export
            </Button>

            <Button
              variant="outline"
              className="bg-white dark:bg-slate-950"
              onClick={() => setShowMenuSync(true)}
            >
              <MdSync className="mr-2 w-4 h-4 text-blue-600 dark:text-blue-500" />
              Sync
            </Button>

            {/* DROPDOWN AKSI LANJUTAN (Menghemat banyak ruang) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-white dark:bg-slate-950"
                >
                  <FaUserGraduate className="mr-2 w-4 h-4" />
                  Update Lulus
                  {/* <ChevronDown className="ml-2 w-4 h-4 text-slate-400" /> */}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={() => setShowUpdateSiswaLulusProfesi(true)}
                >
                  Update Siswa Program Profesi
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowUpdateSiswaLulusRpl(true)}
                >
                  Update Siswa RPL
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* DROPDOWN COLUMNS */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="ml-auto md:ml-0 bg-white dark:bg-slate-950"
                >
                  <Settings2 className="mr-2 w-4 h-4" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* --- BARIS KEDUA: ACTIVE FILTERS (Hanya muncul jika ada filter) --- */}
        {(listJalurJenisAktifFilter?.length > 0 ||
          listProdiAktifFilter?.length > 0) && (
            <div className="flex items-center gap-2 flex-wrap bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 animate-in fade-in duration-300">
              <span className="text-xs font-medium text-slate-500 mr-1">
                Filter Aktif:
              </span>

              {listJalurJenisAktifFilter?.map(
                (item: JalurJenis, index: number) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 font-normal"
                  >
                    {item?.nama_jalur_jenis}
                  </Badge>
                ),
              )}

              {listProdiAktifFilter?.map((item: Unitkerja, index: number) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 font-normal"
                >
                  {item?.nama}
                </Badge>
              ))}
            </div>
          )}
      </div>
      {isPendingDataPendaftaran ? (
        <SkeletonTable />
      ) : (
        <div className="border rounded-xl">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <>
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                          </TableHead>
                          {/* {index === 0 && <TableHead></TableHead>} */}
                        </>
                      );
                    })}
                    {[...Array(4)].map((_, index) => {
                      return (
                        <Fragment key={index}>
                          <TableHead className="whitespace-nowrap">
                            Pilihan ke-{index + 1}
                          </TableHead>
                        </Fragment>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell) => (
                        <>
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>

                          {/* {index === 0 && (
                            <TableCell className="flex gap-3">
                              <Actions row={row.original} />
                              <Drawer>
                                <DrawerTrigger
                                  onClick={() => setSelected(row.original)}
                                >
                                  Lihat
                                </DrawerTrigger>
                                <DrawerContent className="h-[90vh]">
                                  <DrawerHeader>
                                    <DrawerTitle>Data Pendaftaran</DrawerTitle>
                                    <DrawerDescription>
                                      {selected?.data_siswa?.nama_lengkap}
                                    </DrawerDescription>
                                  </DrawerHeader>
                                  <section className="p-5 grid lg:grid-cols-2 grid-cols-1 gap-5">
                                    <div className="flex-1 overflow-auto rounded-xl border">
                                      <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
                                        <tbody>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Nama
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {
                                                selected?.data_siswa
                                                  ?.nama_lengkap
                                              }
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Nomor Pendaftaran
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {selected?.nomor_pendaftaran}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Email
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {selected?.data_siswa?.email}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Jenis Kelamin
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {selected?.data_siswa
                                                ?.jenis_kelamin == "L"
                                                ? "Laki-Laki"
                                                : selected?.data_siswa
                                                      ?.jenis_kelamin == "P"
                                                  ? "Perempuan"
                                                  : "-"}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Tanggal Lahir
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {selected?.data_siswa
                                                ?.tanggal_lahir
                                                ? formatDateIndonesia(
                                                    selected?.data_siswa
                                                      ?.tanggal_lahir,
                                                    "medium",
                                                  )
                                                : "-"}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Jalur
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {
                                                selected?.periode?.jalur_jenis
                                                  ?.nama_jalur_jenis
                                              }
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Tahun Pendaftaran
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {selected?.periode?.tahun}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Pilihan Program Studi
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {selected?.data_pilihan_prodi &&
                                                selected?.data_pilihan_prodi[0]
                                                  ?.unitkerja?.nama}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Pilihan Konsentrasi Program Studi
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {(selected?.data_pilihan_prodi &&
                                                selected?.data_pilihan_prodi[0]
                                                  ?.konsentrasi_prodi
                                                  ?.nama_konsentrasi) ??
                                                "-"}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Status Validasi Pendaftaran
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {!row.original
                                                .is_valid_pendaftaran ||
                                              row.original
                                                .is_valid_pendaftaran == 0 ? (
                                                <Badge variant={"secondary"}>
                                                  Belum Divalidasi Peserta
                                                </Badge>
                                              ) : (
                                                <Badge>
                                                  Sudah Divalidasi Peserta
                                                </Badge>
                                              )}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Status Validasi Pendaftaran
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {!row.original.is_lulus ||
                                              row.original.is_lulus == 0 ? (
                                                <Badge variant={"secondary"}>
                                                  Belum Lulus
                                                </Badge>
                                              ) : (
                                                <Badge>Lulus</Badge>
                                              )}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Status Daftarkembali
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {!row.original
                                                .is_daftar_kembali ||
                                              row.original.is_daftar_kembali ==
                                                0 ? (
                                                <Badge variant={"secondary"}>
                                                  Belum Daftarkembali
                                                </Badge>
                                              ) : (
                                                <Badge>
                                                  Sudah Daftarkembali
                                                </Badge>
                                              )}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              Nominal UKT
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {row.original.data_penetapan_ukt
                                                ?.nominal_ukt
                                                ? formatRupiah(
                                                    Number(
                                                      row.original
                                                        .data_penetapan_ukt
                                                        ?.nominal_ukt,
                                                    ),
                                                  )
                                                : "-"}
                                            </td>
                                          </tr>
                                          <tr>
                                            <th className="py-1 px-3 border font-semibold text-start">
                                              NIM
                                            </th>
                                            <td className="py-1 px-3 border text-center">
                                              :
                                            </td>
                                            <td className="py-1 px-3 border">
                                              {row.original.nim ?? "-"}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>

                                    <div className="flex-1 overflow-auto h-fit rounded-xl border">
                                      <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
                                        <thead>
                                          <tr>
                                            <th className="px-2 py-1 font-semibold border text-start">
                                              Jenis Berkas
                                            </th>
                                            <th className="px-2 py-1 font-semibold border text-start">
                                              Keterangan
                                            </th>
                                            <th className="px-2 py-1 font-semibold border">
                                              Berkas
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {row.original.data_berkas?.length >
                                          0 ? (
                                            row.original.data_berkas?.map(
                                              (dataBerkas) => {
                                                return (
                                                  <tr>
                                                    <td className="px-2 py-1 border">
                                                      {
                                                        dataBerkas?.sys_berkas
                                                          ?.jenis_berkas
                                                          ?.nama_jenis_berkas
                                                      }
                                                    </td>
                                                    <td className="px-2 py-1 border">
                                                      {
                                                        dataBerkas?.sys_berkas
                                                          ?.jenis_berkas
                                                          ?.keterangan
                                                      }
                                                    </td>
                                                    <td className="px-2 py-1 border">
                                                      <div className="flex justify-center">
                                                        <Link
                                                          to={
                                                            dataBerkas?.path_berkas ||
                                                            ""
                                                          }
                                                          target="_blank"
                                                        >
                                                          <Button
                                                            variant={"outline"}
                                                          >
                                                            Lihat Berkas
                                                          </Button>
                                                        </Link>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                );
                                              },
                                            )
                                          ) : (
                                            <tr>
                                              <td
                                                className="px-2 py-1 border"
                                                colSpan={3}
                                              >
                                                Data berkas tidak ditemukan.
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </section>
                                  <DrawerFooter>
                                    <DrawerClose className="w-full">
                                      <Button
                                        variant="default"
                                        className="w-full"
                                      >
                                        Tutup
                                      </Button>
                                    </DrawerClose>
                                  </DrawerFooter>
                                </DrawerContent>
                              </Drawer>
                            </TableCell>
                          )} */}
                        </>
                      ))}
                      {[...Array(4)].map((_, index) => {
                        const pilihanProdi =
                          row?.original.data_pilihan_prodi?.find(
                            (prodi) => prodi.urutan_program_studi === index + 1,
                          );

                        return (
                          <Fragment key={index}>
                            <TableCell>
                              {pilihanProdi ? (
                                <Badge
                                  variant={
                                    pilihanProdi?.is_lulus ? "default" : "dark"
                                  }
                                  className="whitespace-nowrap"
                                >
                                  {pilihanProdi?.unitkerja?.nama}
                                </Badge>
                              ) : (
                                <Badge variant={"outline"}>Kosong</Badge>
                              )}
                            </TableCell>
                          </Fragment>
                        );
                      })}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow>
                        <TableCell colSpan={columns.length + 5} className="p-0">
                          {renderExpandedContent(
                            row.original as DataPendaftaran,
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal
        show={showImport}
        onClose={() => setShowImport(false)}
        closeable={false}
      >
        <FormImport closeModal={() => setShowImport(false)} />
      </Modal>

      <Modal
        show={showImportDataSiswaLulus}
        onClose={() => setShowImportDataSiswaLulus(false)}
        closeable={false}
      >
        <FormImportDataSiswaLulus
          closeModal={() => setShowImportDataSiswaLulus(false)}
        />
      </Modal>

      <Modal
        show={showImportDataSiswaLulusAllPtn}
        onClose={() => setShowDataSiswaLulusAllPtn(false)}
        closeable={false}
      >
        <FormImportDataSiswaLulusAllPtn
          closeModal={() => setShowDataSiswaLulusAllPtn(false)}
        />
      </Modal>

      <Modal
        show={showSync}
        onClose={() => setShowSync(false)}
        closeable={!isPending}
      >
        <form
          onSubmit={handleSyncDataSiswaLulus}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Siswa lulus pada Sistem Seleksi"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          {/* <div>
            <Label>Tahun</Label>
            <Input
              placeholder="Ketik tahun..."
              value={form.tahun}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, tahun: event.target.value }))
              }
            />
          </div> */}
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeSync}
              setOpen={setOpenPeriodeSync}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriode}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPending}
              onClick={() => setShowSync(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showSyncPembayaranPendaftaran}
        onClose={() => setShowSyncPembayaranPendaftaran(false)}
        closeable={!isPendingSyncPembayaranPendaftaran}
      >
        <form
          onSubmit={handleSyncDataPembayaranPendaftaran}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Pembayaran Pendaftaran"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />

          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeSyncPembayaranPendaftaran}
              setOpen={setOpenPeriodeSyncPembayaranPendaftaran}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriode}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncPembayaranPendaftaran}
              onClick={() => setShowSyncPembayaranPendaftaran(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncPembayaranPendaftaran}
            >
              {isPendingSyncPembayaranPendaftaran ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showSyncUkt}
        onClose={() => setShowSyncUkt(false)}
        closeable={!isPendingSyncUkt}
      >
        <form
          onSubmit={handleSyncDataPenetapanUkt}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Penetapan UKT pada Sistem Seleksi"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Combobox
              isPending={isPendingDataPeriode}
              open={openPeriodeSyncUkt}
              setOpen={setOpenPeriodeSyncUkt}
              value={form.id_sys_periode}
              setValue={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }));
              }}
              options={optionsPeriode}
              placeholder="Pilih Periode"
            />
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingSyncUkt}
              onClick={() => setShowSyncUkt(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingSyncUkt}
            >
              {isPendingSyncUkt ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal show={showFilter} onClose={() => setShowFilter(false)} width="2xl">
        <FormFilter closeModal={() => setShowFilter(false)} />
      </Modal>

      <Modal show={showMenuImport} onClose={() => setShowMenuImport(false)}>
        <div className="flex flex-col gap-y-3">
          <FormHeader title="Import Data" icon={<GoPlus />} />
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowImport(true)}
          >
            Import Data Pendaftaran & Pilhan Prodi
            <GoPlus />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowImportDataSiswaLulus(true)}
          >
            Import Data Siswa Lulus
            <GoPlus />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowDataSiswaLulusAllPtn(true)}
          >
            Import Data Siswa Lulus Seluruh PTN
            <GoPlus />
          </Button>
        </div>
      </Modal>

      <Modal show={showMenuSync} onClose={() => setShowMenuSync(false)}>
        <div className="flex flex-col gap-y-3">
          <FormHeader title="Sinkronisasi Data" icon={<MdSync />} />
          {/* <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncUkt(true)}
          >
            Sinkronisasi Hasil Penetapan UKT
            <MdSync />
          </Button> */}
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSync(true)}
          >
            Sinkronisasi Data Siswa Lulus
            <MdSync />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSyncPembayaranPendaftaran(true)}
          >
            Sinkronisasi Pembayaran Pendaftaran
            <MdSync />
          </Button>
        </div>
      </Modal>

      <Modal
        show={showUpdateSiswaLulusProfesi}
        closeable={false}
        className="md:w-[50vw]"
      >
        <FormUpdate
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          listDataPendaftaran={paginationDataPendaftaranProfesi}
          isPendingListDataPendaftaran={isPendingDataPendaftaran}
          closeModal={() => setShowUpdateSiswaLulusProfesi(false)}
        />
      </Modal>

      <Modal
        show={showUpdateSiswaLulusRpl}
        closeable={false}
        className="md:w-[50vw]"
      >
        <FormUpdateRpl
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          listDataPendaftaran={paginationDataPendaftaranRpl}
          isPendingListDataPendaftaran={isPendingDataPendaftaran}
          closeModal={() => setShowUpdateSiswaLulusRpl(false)}
        />
      </Modal>
    </div>
  );
}
